<script setup>
import { useI18n } from 'vue-i18n'
import moment from "moment";
import axios from '@/helpers/axios'
import { getLogNameMeta } from '@/helpers/logs'

definePage({
  meta: {
    layout: 'default',
    role: 'admin'
  },
})

const { t } = useI18n()

const getLogUserMeta = log => {
  if (!log?.user)
    return { label: '-', uuid: null }

  if (typeof log.user === 'object') {
    return {
      label: log.user.name ?? log.user.email ?? log.user.uuid ?? '-',
      uuid: log.user.uuid ?? null,
    }
  }

  return { label: log.user, uuid: null }
}

const searchQuery = ref('')
const rowPerPage = ref(10)
const currentPage = ref(1)
const totalPage = ref(1)
const totalItems = ref(0)
const items = ref([])

watchEffect(() => {
  axios.get('/admin/logs', {
    params: {
      q: searchQuery.value,
      perPage: rowPerPage.value,
      currentPage: currentPage.value,
    },
  }).then(response => {
    items.value = response.data.data
    totalPage.value = response.data.meta.last_page
    totalItems.value = response.data.meta.total
  }).catch(e => {
    console.error(e)
  })
})


watchEffect(() => {
  if (currentPage.value > totalPage.value)
    currentPage.value = totalPage.value
})


// 👉 Computing pagination data
const paginationData = computed(() => {
  const firstIndex = items.value.length ? (currentPage.value - 1) * rowPerPage.value + 1 : 0
  const lastIndex = items.value.length + (currentPage.value - 1) * rowPerPage.value
  
  return t('tables.showing', { firstIndex, lastIndex, totalItems: totalItems.value })
})
</script>

<template>
  <VCard
    v-if="items"
    id="items-list"
    :title="$t('logs.title')"
  >
    <VCardText class="d-flex align-center flex-wrap gap-4">
      <!-- 👉 Rows per page -->
      <div
        class="d-flex align-center"
        style="width: 150px;"
      >
        <span class="text-no-wrap me-3">{{ $t('tables.show') }}</span>
        <VSelect
          v-model="rowPerPage"
          density="compact"
          :items="[10, 20, 30, 50]"
        />
      </div>

      <VSpacer />

      <div class="d-flex align-center flex-wrap gap-4">
        <!-- 👉 Search  -->
        <div class="items-list-filter">
          <VTextField
            v-model="searchQuery"
            :placeholder="$t('logs.search_log')"
            density="compact"
          />
        </div>
      </div>
    </VCardText>

    <VDivider />

    <!-- SECTION Table -->
    <VTable class="text-no-wrap items-list-table">
      <!-- 👉 Table head -->
      <thead class="text-uppercase">
        <tr>
          <th scope="col">
            {{ $t('logs.module') }}
          </th>

          <th scope="col">
            {{ $t('logs.user') }}
          </th>

          <th scope="col">
            {{ $t('logs.description') }}
          </th>

          <th scope="col">
            {{ $t('logs.date') }}
          </th>
        </tr>
      </thead>

      <!-- 👉 Table Body -->
      <tbody>
        <tr
          v-for="log in items"
          :key="log.uuid"
          style="height: 3.75rem;"
        >
          <!-- 👉 Module -->
          <td>
            <VChip
              class="text-capitalize font-weight-medium"
              density="comfortable"
              variant="tonal"
              size="small"
              :color="getLogNameMeta(log.log_name).color"
            >
              {{ getLogNameMeta(log.log_name).label }}
            </VChip>
          </td>

          <!-- 👉 User -->
          <td>
            <RouterLink
              v-if="getLogUserMeta(log).uuid"
              :to="{ name: 'admin-users-uuid', params: { uuid: getLogUserMeta(log).uuid } }"
            >
              {{ getLogUserMeta(log).label }}
            </RouterLink>
            <span v-else>
              {{ getLogUserMeta(log).label }}
            </span>
          </td>

          <!-- 👉 Description -->
          <td>
            {{ log.description }}
          </td>

          <!-- 👉 Date -->
          <td>
            {{ moment.utc(log.created_at).local().format("Y-MM-DD HH:mm") }}
          </td>
        </tr>
      </tbody>

      <!-- 👉 table footer  -->
      <tfoot v-show="!items.length">
        <tr>
          <td
            colspan="8"
            class="text-center text-body-1"
          >
            {{ $t('logs.no_logs') }}
          </td>
        </tr>
      </tfoot>
    </VTable>
    <!-- !SECTION -->

    <VDivider />

    <!-- SECTION Pagination -->
    <VCardText class="d-flex align-center flex-wrap gap-4 py-3">
      <!-- 👉 Pagination meta -->
      <span class="text-sm text-disabled">
        {{ paginationData }}
      </span>

      <VSpacer />

      <!-- 👉 Pagination -->
      <VPagination
        v-model="currentPage"
        size="small"
        :total-visible="5"
        :length="totalPage"
      />
    </VCardText>
    <!-- !SECTION -->
  </VCard>
</template>

<style lang="scss">
#items-list {
  .items-list-actions {
    inline-size: 8rem;
  }

  .items-list-filter {
    inline-size: 12rem;
  }
}
</style>
